/**
 * 振り返りで聞く「観点」の正本（PRODUCT_DEV-944）。
 *
 * 観点は **共通（法人／個人で1組ずつ・全テナント同じ）＋ プリセットごとに足した観点** で決まる。
 * 何を聞くかを知りたい箇所（プロンプト・ツール定義・進み具合・振り返りの表示）は、
 * 固定の一覧を直接読まず {@link resolveReviewPerspectives} を通すこと。
 *
 * ## 決めてあること（2026-09-18 ユーザ判断）
 *
 * - Unizo の芯は「キーマン（最終決裁者）の特定と、そこへつながったか」。法人商談では
 *   「課題感の重さ」（商談記録の課題と重なる）を外し、「決裁・進め方の空気」を
 *   キーマンに統合する
 * - 個人商談は本人が決裁者なのでキーマンは当てはまらない。代わりに「決め方と時期」を聞く
 * - 共通の観点は**全テナント同じ**（テナントでは変えない）。会社ごとの違いはプリセットの追加観点で出す
 * - PRODUCT_DEV-872: 共通の観点の「聞き方」「何を書くか」は Admin の ONP
 *   （MINUTES_REVIEW_PERSPECTIVES・全テナント共通）で上書きできる。id と見出しは変えられない
 *   （保存キー・ツールのプロパティ名・表示の見出しなので）。上書きは
 *   {@link parseReviewPerspectiveOverrides} で読み、{@link resolveReviewPerspectives} の第2引数に渡す
 * - 追加観点は商談プリセットの中の欄（`reviewPerspectives`）。別プリセットにはしない
 *   （ユーザーが選ぶプリセット一覧に出さないため）
 * - 追加観点は商談項目に紐づけられる。埋まっていれば聞かず、空なら聞いてその項目へ書く
 *
 * ⚠ 観点の id は Function Calling のプロパティ名と `minutes_review` の保存キーになる。
 * 英数と `_` だけにする（{@link REVIEW_PERSPECTIVE_ID_RE}）。
 *
 * 純関数（web / api の両方から使う）。
 */
import { effectivePresetFieldId, isIndividualPresetFields } from "./presetSubject";

export interface ReviewPerspective {
  id: string;
  label: string;
  /** その観点を引き出すときの聞き方（プロンプトの例示に使う）。 */
  question: string;
  /** その観点へ何を書くか（ツール定義・抽出プロンプトの description になる）。 */
  description: string;
  /**
   * 紐づけた商談項目の id（プリセットの追加観点だけ）。紐づけた観点の答えは
   * `minutes_review` ではなくこの項目へ書く。
   */
  linkedFieldId?: string;
}

/** プリセットに保存する追加観点の形（contract の ReviewPerspectiveSchema と同じ）。 */
export interface PresetReviewPerspective {
  id: string;
  label: string;
  question: string;
  linkedFieldId?: string;
}

// ─── 共通の観点 ──────────────────────────────────────────

export const REVIEW_HIGHLIGHT: ReviewPerspective = {
  id: "highlight",
  label: "盛り上がり・手応え",
  question:
    "雑談でもよいので、一番盛り上がった話・相手が前のめりになった場面は何ですか？",
  description:
    "相手が前のめりになった・食いついた場面。どの話題で、誰が、どう反応したか（声が大きくなった/身を乗り出した/メモを取った 等）。雑談で盛り上がった話も含む",
};

export const REVIEW_CONCERN: ReviewPerspective = {
  id: "concern",
  label: "難色・懸念のサイン",
  question: "難しい顔をしていた方はいましたか？それはどの話のときでしたか？",
  description:
    "表情が曇った・沈黙した・話題を変えた・言葉を濁した場面。どの話題のときに、誰が、どんな様子だったか",
};

export const REVIEW_UNSPOKEN: ReviewPerspective = {
  id: "unspoken",
  label: "言葉にならなかった気づき",
  question: "言葉にはなっていないけれど、感じ取ったことはありますか？",
  description:
    "発言としては出ていないが、その場にいたから分かったこと。場の空気、帰り際の様子、雰囲気の変わり目など",
};

/**
 * 法人商談のキーマン（最終決裁者）。「決裁・進め方の空気」を統合した。
 * 商談記録・議事録にすでに書かれていれば、聞き直さず確認だけにする（判定は AI が記録全体を読んで行う）。
 */
export const REVIEW_KEYMAN: ReviewPerspective = {
  id: "keyman",
  label: "キーマン（最終決裁者）",
  question:
    "今日の相手の中で、最終的に決めるのはどなたでしたか？その方とは話せましたか？",
  description:
    "①最終決裁者は誰か（名前・役職。分からなければ分からないと書く）②その人と話せた・つながったか ③つながっていなければ、次にどうつなぐか（ネクストアクション）。商談記録や議事録にすでに書かれていれば、聞き直さず確認だけにする",
};

/** 個人商談の「決め方と時期」。本人が決裁者でも、相談相手と時期で次の一手が変わる。 */
export const REVIEW_DECIDE_TIMING: ReviewPerspective = {
  id: "decide_timing",
  label: "決め方と時期",
  question:
    "ご本人は誰かに相談してから決めそうでしたか？いつ頃までに決めそうでしたか？",
  description:
    "本人がその場で決めるのか、家族など誰かに相談してから決めるのか（相談相手は誰か）。いつ頃までに決めそうか、そう感じた根拠",
};

/** 法人商談の共通の観点（この順に聞く）。 */
export const COMMON_CORPORATE_REVIEW_PERSPECTIVES: readonly ReviewPerspective[] = [
  REVIEW_HIGHLIGHT,
  REVIEW_CONCERN,
  REVIEW_KEYMAN,
  REVIEW_UNSPOKEN,
];

/** 個人商談の共通の観点（この順に聞く）。 */
export const COMMON_INDIVIDUAL_REVIEW_PERSPECTIVES: readonly ReviewPerspective[] = [
  REVIEW_HIGHLIGHT,
  REVIEW_CONCERN,
  REVIEW_DECIDE_TIMING,
  REVIEW_UNSPOKEN,
];

/**
 * もう聞かない観点（PRODUCT_DEV-944 で外した）。**保存済みの値を表示するときのラベルにだけ使う。**
 * 聞く側の一覧（プロンプト・ツール・進み具合）には入れない。
 */
export const RETIRED_REVIEW_PERSPECTIVES: readonly ReviewPerspective[] = [
  {
    id: "pain",
    label: "課題感の重さ",
    question: "課題の話の中では、どれが一番重そうでしたか？",
    description:
      "相手が語った課題のうち、どれが一番重く受け止められていたか。そう感じた根拠（語気が強かった/何度も戻ってきた/具体的な数字が出た 等）",
  },
  {
    id: "decision",
    label: "決裁・進め方の空気",
    question: "その場で決める立場の方は誰でしたか？その方の反応はどうでしたか？",
    description:
      "誰が主導していたか・決裁者らしき人は誰か、その人の反応。同席者どうしのやりとり（顔を見合わせた/上司の顔色を見た 等）や、進め方に対する温度感",
  },
];

// ─── 共通の観点の文言の上書き（PRODUCT_DEV-872） ─────────────

/** 文言を上書きできる共通の観点（法人・個人をあわせた5つ。ONP の既定の本文に並ぶ順）。 */
export const EDITABLE_COMMON_REVIEW_PERSPECTIVES: readonly ReviewPerspective[] = [
  REVIEW_HIGHLIGHT,
  REVIEW_CONCERN,
  REVIEW_KEYMAN,
  REVIEW_DECIDE_TIMING,
  REVIEW_UNSPOKEN,
];

/** 共通の観点の文言の上書き（観点 id → 変える項目だけ）。 */
export type ReviewPerspectiveOverrides = Readonly<
  Record<string, { question?: string; description?: string } | undefined>
>;

const OVERRIDE_HEADING_RE = /^##\s*([A-Za-z_][A-Za-z0-9_]*)/;
const OVERRIDE_QUESTION_RE = /^聞き方\s*[:：]\s*(.*)$/;
const OVERRIDE_DESCRIPTION_RE = /^書くこと\s*[:：]\s*(.*)$/;

/**
 * ONP `MINUTES_REVIEW_PERSPECTIVES` の既定の本文。観点ごとに次の3行を並べる。
 *
 *   ## <id>（見出し）
 *   聞き方: <その観点を引き出すときの聞き方>
 *   書くこと: <その観点へ何を書くか>
 *
 * 見出しは読む人のための表示で、書き換えても効かない。
 * 「聞き方」「書くこと」は改行して続きを書ける（次の「聞き方:」「書くこと:」「##」までがその項目）。
 */
export const formatReviewPerspectiveTexts = (
  perspectives: readonly ReviewPerspective[] = EDITABLE_COMMON_REVIEW_PERSPECTIVES,
): string =>
  perspectives
    .map((p) => `## ${p.id}（${p.label}）\n聞き方: ${p.question}\n書くこと: ${p.description}`)
    .join("\n\n");

/**
 * ONP の本文を読む。読めた上書きと、Admin の画面に出す注意（日本語の文）を返す。
 * - 共通の観点の id 以外の見出しは無視する
 * - 「聞き方」「書くこと」が無い・空の項目は上書きしない（コードの既定のまま）
 */
export const readReviewPerspectiveTexts = (
  content: string | null | undefined,
): { overrides: ReviewPerspectiveOverrides; problems: string[] } => {
  const overrides: Record<string, { question?: string; description?: string }> = {};
  const problems: string[] = [];
  if (!content?.trim()) return { overrides, problems };

  const editable = new Set(EDITABLE_COMMON_REVIEW_PERSPECTIVES.map((p) => p.id));
  const seen = new Set<string>();
  let current: string | null = null;
  // 直前に読んだ項目。続きの行（改行して書いた2行目以降）をここへつなぐ
  let field: "question" | "description" | null = null;
  const append = (key: "question" | "description", text: string, fresh: boolean): void => {
    if (!current || !text) return;
    const target = overrides[current]!;
    target[key] = fresh || !target[key] ? text : `${target[key]}\n${text}`;
  };
  for (const raw of content.split(/\r?\n/)) {
    const line = raw.trim();
    const heading = line.match(OVERRIDE_HEADING_RE);
    if (heading) {
      const id = heading[1]!;
      field = null;
      if (!editable.has(id)) {
        current = null;
        problems.push(`「${id}」は共通の観点ではないので無視されます`);
      } else {
        if (seen.has(id)) problems.push(`「${id}」が2回書かれています。後に書いた方を使います`);
        seen.add(id);
        current = id;
        overrides[id] = {};
      }
      continue;
    }
    if (!current || !line) continue;
    const question = line.match(OVERRIDE_QUESTION_RE);
    if (question) {
      field = "question";
      append("question", question[1]!.trim(), true);
      continue;
    }
    const description = line.match(OVERRIDE_DESCRIPTION_RE);
    if (description) {
      field = "description";
      append("description", description[1]!.trim(), true);
      continue;
    }
    // 「聞き方:」「書くこと:」の続きの行は、その項目へ改行でつなぐ（黙って捨てない）
    if (field) append(field, line, false);
    else problems.push(`「${current}」に読めない行があります: ${line.slice(0, 20)}`);
  }

  for (const p of EDITABLE_COMMON_REVIEW_PERSPECTIVES) {
    const o = overrides[p.id];
    const missing = [
      ...(o?.question ? [] : ["聞き方"]),
      ...(o?.description ? [] : ["書くこと"]),
    ];
    if (missing.length > 0) {
      problems.push(`「${p.id}（${p.label}）」の${missing.join("・")}が読めないので、既定の文を使います`);
    }
  }
  return { overrides, problems };
};

/** ONP の本文から上書きだけを取り出す（未設定・空なら上書きなし）。 */
export const parseReviewPerspectiveOverrides = (
  content: string | null | undefined,
): ReviewPerspectiveOverrides => readReviewPerspectiveTexts(content).overrides;

const applyReviewPerspectiveOverride = (
  p: ReviewPerspective,
  overrides: ReviewPerspectiveOverrides | null | undefined,
): ReviewPerspective => {
  const o = overrides?.[p.id];
  if (!o?.question && !o?.description) return p;
  return {
    ...p,
    ...(o.question ? { question: o.question } : {}),
    ...(o.description ? { description: o.description } : {}),
  };
};

// ─── 追加観点の検査 ──────────────────────────────────────

/** 観点 id に使える文字（ツールのプロパティ名・保存キーになるため英数と `_` のみ）。 */
export const REVIEW_PERSPECTIVE_ID_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;

/** 追加観点の最大数（振り返りが長くなりすぎないように）。 */
export const MAX_PRESET_REVIEW_PERSPECTIVES = 10;

/**
 * 追加観点が使ってはいけない id。共通・もう聞かない観点と、振り返り本文の項目 id
 * （`MINUTES_REVIEW_FIELD_ID`。循環 import を避けるため文字列で持ち、テストで一致を見ている）。
 */
export const RESERVED_REVIEW_PERSPECTIVE_IDS: ReadonlySet<string> = new Set([
  ...COMMON_CORPORATE_REVIEW_PERSPECTIVES.map((p) => p.id),
  ...COMMON_INDIVIDUAL_REVIEW_PERSPECTIVES.map((p) => p.id),
  ...RETIRED_REVIEW_PERSPECTIVES.map((p) => p.id),
  "minutes_review",
]);

/** 振り返りの観点を紐づけられない項目（日付と振り返り本文そのもの）。 */
const UNLINKABLE_FIELD_IDS: ReadonlySet<string> = new Set(["meeting_date", "minutes_review"]);

/**
 * 紐づけ先にできる項目 id。**無効（enabled: false）の項目は外す** — 録音中のツール定義にも
 * 画面の項目にも出ないので、紐づけると答えがどこにも保存されない。
 */
const linkableFieldIds = (
  fields: ReadonlyArray<{ id?: string; $ref?: string; enabled?: boolean }> | undefined,
): Set<string> =>
  new Set(
    (fields ?? [])
      .filter((f) => f.enabled !== false)
      .map((f) => effectivePresetFieldId(f))
      .filter((id): id is string => !!id && !UNLINKABLE_FIELD_IDS.has(id)),
  );

/**
 * プリセットの追加観点を検査し、問題を日本語の文で返す（空なら問題なし）。
 * API（保存時）と編集画面（保存前）で同じものを使う。
 */
export const validatePresetReviewPerspectives = (
  perspectives: readonly PresetReviewPerspective[] | undefined,
  fields: ReadonlyArray<{ id?: string; $ref?: string; enabled?: boolean }> | undefined,
): string[] => {
  const list = perspectives ?? [];
  const errors: string[] = [];
  if (list.length > MAX_PRESET_REVIEW_PERSPECTIVES) {
    errors.push(`振り返りで足す観点は ${MAX_PRESET_REVIEW_PERSPECTIVES} 個までです`);
  }
  const fieldIds = linkableFieldIds(fields);
  const seen = new Set<string>();
  list.forEach((p, i) => {
    const at = `振り返りで足す観点の ${i + 1} 行目`;
    if (!REVIEW_PERSPECTIVE_ID_RE.test(p.id)) errors.push(`${at}: id に使えない文字があります`);
    if (RESERVED_REVIEW_PERSPECTIVE_IDS.has(p.id)) errors.push(`${at}: id「${p.id}」は共通の観点と重なります`);
    if (seen.has(p.id)) errors.push(`${at}: id「${p.id}」が重複しています`);
    seen.add(p.id);
    if (!p.label.trim()) errors.push(`${at}: 見出しが空です`);
    if (!p.question.trim()) errors.push(`${at}: 聞き方が空です`);
    if (p.linkedFieldId !== undefined) {
      if (UNLINKABLE_FIELD_IDS.has(p.linkedFieldId)) {
        errors.push(`${at}: この項目には紐づけられません`);
      } else if (!fieldIds.has(p.linkedFieldId)) {
        errors.push(`${at}: 紐づけた項目がプリセットに無いか、無効になっています`);
      }
    }
  });
  return errors;
};

// ─── 解決 ────────────────────────────────────────────────

export interface ReviewPerspectivePresetLike {
  fields?: ReadonlyArray<{ id?: string; $ref?: string; enabled?: boolean }>;
  reviewPerspectives?: readonly PresetReviewPerspective[];
}

/**
 * その商談で聞く観点の一覧（共通 → プリセットの追加観点の順）。
 *
 * - 法人／個人はプリセットに customer_info があるかで決める（{@link isIndividualPresetFields}）
 * - 追加観点のうち、id が共通と重なるもの・重複・不正なものは黙って外す（保存時の検査を
 *   すり抜けた古いデータでも振り返りを止めない）
 * - 紐づけ先の項目がプリセットから消えていた・無効になっていたら、紐づけ無しとして扱う
 *   （`minutes_review` へ書く）
 * - `overrides`（PRODUCT_DEV-872）は共通の観点の聞き方・何を書くかだけを差し替える。
 *   AI に渡す文を作る所（プロンプト・ツール定義・抽出）が渡す。id と見出ししか使わない所
 *   （保存値の検証・表示）は渡さなくてよい
 */
export const resolveReviewPerspectives = (
  preset: ReviewPerspectivePresetLike | null | undefined,
  overrides?: ReviewPerspectiveOverrides | null,
): ReviewPerspective[] => {
  const common = (
    isIndividualPresetFields(preset?.fields)
      ? COMMON_INDIVIDUAL_REVIEW_PERSPECTIVES
      : COMMON_CORPORATE_REVIEW_PERSPECTIVES
  ).map((p) => applyReviewPerspectiveOverride(p, overrides));
  const fieldIds = linkableFieldIds(preset?.fields);
  const seen = new Set<string>();
  const additions: ReviewPerspective[] = [];
  for (const p of preset?.reviewPerspectives ?? []) {
    if (!REVIEW_PERSPECTIVE_ID_RE.test(p.id) || RESERVED_REVIEW_PERSPECTIVE_IDS.has(p.id) || seen.has(p.id)) {
      continue;
    }
    if (!p.label?.trim() || !p.question?.trim()) continue;
    seen.add(p.id);
    const linked = p.linkedFieldId && fieldIds.has(p.linkedFieldId) ? p.linkedFieldId : undefined;
    additions.push({
      id: p.id,
      label: p.label.trim(),
      question: p.question.trim(),
      description: p.question.trim(),
      ...(linked ? { linkedFieldId: linked } : {}),
    });
    if (additions.length >= MAX_PRESET_REVIEW_PERSPECTIVES) break;
  }
  return [...common, ...additions];
};

/** 値が「埋まっている」か（空文字・空白・空配列・空オブジェクトは空）。 */
const isFilledValue = (v: unknown): boolean => {
  if (v == null) return false;
  if (typeof v === "string") {
    const t = v.trim();
    // 階層項目・担当者は JSON 文字列で持ち込まれる（`{"a":""}` は中身が空）
    if (t.startsWith("{") || t.startsWith("[")) {
      try {
        return isFilledValue(JSON.parse(t));
      } catch {
        return true;
      }
    }
    return t.length > 0;
  }
  if (Array.isArray(v)) return v.some(isFilledValue);
  if (typeof v === "object") return Object.values(v as Record<string, unknown>).some(isFilledValue);
  return true;
};

/**
 * 振り返りで実際に聞く観点。紐づけた商談項目がすでに埋まっている観点は外す。
 * `filledFieldValues` は商談の既存値（項目 id → 値）。
 */
export const askableReviewPerspectives = (
  perspectives: readonly ReviewPerspective[],
  filledFieldValues: Readonly<Record<string, unknown>> | null | undefined,
): ReviewPerspective[] =>
  perspectives.filter(
    (p) => !p.linkedFieldId || !isFilledValue(filledFieldValues?.[p.linkedFieldId]),
  );

/** `minutes_review` に保存する観点（紐づけの無いもの）。紐づけた観点の答えは商談項目へ書く。 */
export const minutesReviewStoredPerspectives = (
  perspectives: readonly ReviewPerspective[],
): ReviewPerspective[] => perspectives.filter((p) => !p.linkedFieldId);

/** 表示用に引けるラベル（共通の観点・もう聞かない観点）。 */
const KNOWN_REVIEW_PERSPECTIVES: ReadonlyMap<string, ReviewPerspective> = new Map(
  [
    ...COMMON_CORPORATE_REVIEW_PERSPECTIVES,
    ...COMMON_INDIVIDUAL_REVIEW_PERSPECTIVES,
    ...RETIRED_REVIEW_PERSPECTIVES,
  ].map((p) => [p.id, p]),
);

/**
 * 保存済みの振り返りを表示するときの見出し。聞く観点に加えて、**保存値がある観点は全部**
 * 末尾に出す（過去の記録を消さない）。もう聞かない観点（課題感の重さ 等）・プリセットから外した
 * 追加観点・法人／個人が入れ替わったときの相手側の共通観点がこれに当たる。ラベルが引けない
 * ものは id のまま出す。
 */
export const reviewPerspectivesForDisplay = (
  perspectives: readonly ReviewPerspective[],
  storedSlotIds: Iterable<string>,
): ReviewPerspective[] => {
  const shown = minutesReviewStoredPerspectives(perspectives);
  const ids = new Set(shown.map((p) => p.id));
  const extra: ReviewPerspective[] = [];
  for (const id of storedSlotIds) {
    if (ids.has(id) || perspectives.some((p) => p.id === id)) continue;
    ids.add(id);
    extra.push(KNOWN_REVIEW_PERSPECTIVES.get(id) ?? { id, label: id, question: "", description: "" });
  }
  return [...shown, ...extra];
};
